import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { isSessionExpiredError, triggerSessionExpired } from '@/lib/sessionRecovery';

const KIOSK_REGISTER_DRAFT_KEY = 'kiosk-register-draft-v1';

const getStoreLabel = (store) => {
  if (!store) return '';
  if (store.includes('perth')) return 'Perth';
  if (store.includes('singapore')) return 'Singapore';
  if (store.includes('internal')) return 'Internal';
  return store;
};

const KioskRegisterPage = () => {
  const { sessionModalOpen } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [userStore, setUserStore] = useState('');

  // Staff action state
  const [registrationProcessing, setRegistrationProcessing] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [showStaffActions, setShowStaffActions] = useState(false);

  // Track if draft has been restored to avoid overwriting on initial render
  const draftRestoredRef = useRef(false);

  useEffect(() => {
    try {
      const savedDraft = localStorage.getItem(KIOSK_REGISTER_DRAFT_KEY);
      if (!savedDraft) {
        draftRestoredRef.current = true; // No draft to restore, allow saving
        return;
      }
      const parsedDraft = JSON.parse(savedDraft);
      if (parsedDraft?.formData) setFormData(parsedDraft.formData);
      if (parsedDraft?.successData) setSuccessData(parsedDraft.successData);
      if (typeof parsedDraft?.emailSent === 'boolean') setEmailSent(parsedDraft.emailSent);
      if (typeof parsedDraft?.showStaffActions === 'boolean') setShowStaffActions(parsedDraft.showStaffActions);
      if (typeof parsedDraft?.registrationComplete === 'boolean') setRegistrationComplete(parsedDraft.registrationComplete);
      // Mark draft as restored after state updates are scheduled
      setTimeout(() => { draftRestoredRef.current = true; }, 0);
    } catch (error) {
      console.warn('Failed to restore kiosk registration draft', error);
      draftRestoredRef.current = true; // Allow saving even if restore failed
    }
  }, []);

  useEffect(() => {
    // Skip saving until draft has been restored to avoid overwriting with initial empty values
    if (!draftRestoredRef.current) return;
    const draftPayload = {
      formData,
      successData,
      emailSent,
      showStaffActions,
      registrationComplete,
    };
    localStorage.setItem(KIOSK_REGISTER_DRAFT_KEY, JSON.stringify(draftPayload));
  }, [formData, successData, emailSent, showStaffActions, registrationComplete]);

  // Get user's store on mount
  useEffect(() => {
    const fetchUserStore = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/auth/me', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        // Assuming user has stores array, take the first one
        const stores = response.data.stores || response.data.assigned_stores || [];
        setUserStore(stores[0] || response.data.store || '');
      } catch (error) {
        console.error('Failed to fetch user store:', error);
      }
    };
    fetchUserStore();
  }, []);

  const validateEmail = (email) => {
    // Check for common typos
    if (email.includes(',')) {
      return 'Email contains comma. Please use dot (.) instead.';
    }
    if (email.includes(' ')) {
      return 'Email should not contain spaces.';
    }
    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address.';
    }
    return null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate email
    const emailError = validateEmail(formData.email);
    if (emailError) {
      toast.error(emailError);
      return;
    }

    setIsSubmitting(true);
    setSuccessData({
      name: `${formData.firstName} ${formData.lastName}`.trim(),
      email: formData.email,
      phone: formData.phone,
      store: userStore,
      customer_id: null,
    });
    setRegistrationComplete(false);
    setEmailSent(false);
    setShowStaffActions(false);
    setIsSubmitting(false);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleRegisterAnother = () => {
    setSuccessData(null);
    setEmailSent(false);
    setShowStaffActions(false);
    setRegistrationComplete(false);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: ''
    });
    localStorage.removeItem(KIOSK_REGISTER_DRAFT_KEY);
  };

  const handleEditDetails = () => {
    if (!successData) return;
    const [firstName = '', ...rest] = String(successData.name || '').split(' ');
    setFormData({
      firstName,
      lastName: rest.join(' '),
      email: successData.email || '',
      phone: successData.phone || '',
    });
    setSuccessData(null);
    setShowStaffActions(false);
    setRegistrationComplete(false);
  };

  const handleFinalizeRegistration = async (sendWelcomeEmail) => {
    if (!successData) return;
    setRegistrationProcessing(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        triggerSessionExpired({ reason: 'Your session expired while registering a client. Log in to continue from this form.' });
        return;
      }

      const createResponse = await axios.post(
        '/api/customers',
        {
          customer_name: successData.name,
          email: successData.email,
          phone_number: successData.phone,
          store: successData.store || 'singapore',
        },
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      const createdCustomerId = createResponse?.data?.customer_id || null;

      if (sendWelcomeEmail) {
        await axios.post(
          '/api/emails/welcome',
          {
            customer_name: successData.name,
            customer_email: successData.email,
            customer_id: createdCustomerId,
            store: successData.store || 'singapore',
          },
          { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
      }

      setSuccessData((prev) => ({ ...(prev || {}), customer_id: createdCustomerId }));
      setEmailSent(Boolean(sendWelcomeEmail));
      setRegistrationComplete(true);
      setShowStaffActions(true);
      localStorage.removeItem(KIOSK_REGISTER_DRAFT_KEY);
      toast.success(sendWelcomeEmail ? 'Client registered and welcome email sent' : 'Client registered successfully');
    } catch (err) {
      if (isSessionExpiredError(err)) {
        triggerSessionExpired({ reason: 'Your session expired while registering a client. Please log in again; your entered details are still saved here.' });
        return;
      }
      const detail = err.response?.data?.detail;
      const errorMsg =
        (Array.isArray(detail) && detail.map((item) => item?.msg).filter(Boolean).join(', ')) ||
        (typeof detail === 'string' ? detail : null) ||
        err.response?.data?.message ||
        err.message ||
        'Failed to register client';
      toast.error(errorMsg);
    } finally {
      setRegistrationProcessing(false);
    }
  };

  // Verification / completion screen
  if (successData) {
    const storeLabel = getStoreLabel(successData.store);
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-8">
        <Card className="w-full max-w-3xl shadow-2xl">
          <CardHeader className="text-center pb-6">
            <div className="flex justify-center mb-4">
              <CheckCircle2 className={`w-24 h-24 ${registrationComplete ? 'text-green-600' : 'text-blue-600'}`} />
            </div>
            <CardTitle className={`text-5xl font-bold mb-2 ${registrationComplete ? 'text-green-700' : 'text-blue-700'}`}>
              {registrationComplete ? 'Registration Completed!' : 'Please Verify Details'}
            </CardTitle>
            <p className="text-2xl text-slate-600 mt-4">
              {registrationComplete
                ? 'Client details have been saved successfully'
                : 'Confirm these details before final registration'}
            </p>
          </CardHeader>

          <CardContent className="space-y-8">
            <div className="grid grid-cols-1 gap-6">
              <div className="bg-slate-50 rounded-xl p-6">
                <p className="text-xl text-slate-500 mb-2">Name</p>
                <p className="text-3xl font-semibold text-slate-800">{successData.name}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-6">
                <p className="text-xl text-slate-500 mb-2">Email</p>
                <p className="text-3xl font-semibold text-slate-800">{successData.email}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-6">
                <p className="text-xl text-slate-500 mb-2">Phone</p>
                <p className="text-3xl font-semibold text-slate-800">{successData.phone}</p>
              </div>
              {successData.store && (
                <div className="bg-slate-50 rounded-xl p-6">
                  <p className="text-xl text-slate-500 mb-2">Store</p>
                  <p className="text-3xl font-semibold text-slate-800">{storeLabel || successData.store}</p>
                </div>
              )}
            </div>

            {!registrationComplete && (
              <div className="pt-1">
                <Button
                  variant="outline"
                  onClick={handleEditDetails}
                  className="w-full h-14 text-lg font-medium"
                  data-testid="kiosk-edit-details-button"
                  disabled={registrationProcessing}
                >
                  Edit Details
                </Button>
              </div>
            )}

            <div className="pt-2 flex justify-end">
              {!showStaffActions ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowStaffActions(true)}
                  className="text-xs text-slate-400 hover:text-slate-600"
                  data-testid="kiosk-staff-actions-toggle"
                >
                  Staff actions
                </Button>
              ) : (
                <div className="w-full flex flex-col gap-4" data-testid="kiosk-staff-actions-panel">
                  {!registrationComplete ? (
                    <>
                      {sessionModalOpen ? (
                        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-4 py-3" data-testid="kiosk-session-expired-banner">
                          Session expired. Log in using the popup to continue without losing these details.
                        </p>
                      ) : null}
                      <Button
                        onClick={() => handleFinalizeRegistration(true)}
                        className="w-full h-20 text-2xl font-bold bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center gap-3"
                        data-testid="kiosk-register-and-send-email-button"
                        disabled={registrationProcessing}
                      >
                        {registrationProcessing ? <Loader2 className="w-8 h-8 animate-spin" /> : <Mail className="w-8 h-8" />}
                        Register Client + Send Welcome Email
                      </Button>

                      <Button
                        onClick={() => handleFinalizeRegistration(false)}
                        variant="outline"
                        className="w-full h-16 text-xl font-semibold border-2 border-slate-300 text-slate-700 hover:bg-slate-100"
                        data-testid="kiosk-register-no-email-button"
                        disabled={registrationProcessing}
                      >
                        {registrationProcessing ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                        Register Client
                      </Button>

                      <Button
                        variant="ghost"
                        onClick={() => setShowStaffActions(false)}
                        className="w-full text-sm text-slate-500 hover:text-slate-700"
                        data-testid="kiosk-close-staff-actions-button"
                      >
                        Close Staff Actions
                      </Button>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center justify-center gap-3 bg-emerald-50 border border-emerald-300 rounded-xl p-6">
                        <CheckCircle2 className="w-10 h-10 text-emerald-600 flex-shrink-0" />
                        <div>
                          <p className="text-2xl font-bold text-emerald-700">Client Registered!</p>
                          <p className="text-lg text-emerald-600 mt-1">
                            {emailSent ? 'Welcome email was sent.' : 'No email was sent.'}
                          </p>
                        </div>
                      </div>

                      <Button
                        variant="outline"
                        onClick={handleRegisterAnother}
                        className="w-full h-16 text-xl font-semibold border-2 border-slate-300 text-slate-700 hover:bg-slate-100"
                        data-testid="kiosk-register-another-client-button"
                      >
                        Register Another Client
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Registration form
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-8">
      <Card className="w-full max-w-3xl shadow-2xl">
        <CardHeader className="text-center pb-6">
          <CardTitle className="text-5xl font-bold text-slate-800 mb-2">
            Perfect Attire
          </CardTitle>
          <p className="text-3xl text-slate-600 mt-2">New Customer Registration</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* First Name */}
            <div>
              <Label htmlFor="firstName" className="text-2xl font-semibold text-slate-700 mb-3 block">
                First Name *
              </Label>
              <Input
                id="firstName"
                type="text"
                required
                value={formData.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                className="h-16 text-2xl px-6"
                placeholder="Enter first name"
                autoComplete="given-name"
              />
            </div>

            {/* Last Name */}
            <div>
              <Label htmlFor="lastName" className="text-2xl font-semibold text-slate-700 mb-3 block">
                Last Name *
              </Label>
              <Input
                id="lastName"
                type="text"
                required
                value={formData.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                className="h-16 text-2xl px-6"
                placeholder="Enter last name"
                autoComplete="family-name"
              />
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-2xl font-semibold text-slate-700 mb-3 block">
                Email Address *
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value.toLowerCase().trim())}
                className="h-16 text-2xl px-6"
                placeholder="example@email.com"
                autoComplete="email"
              />
            </div>

            {/* Phone */}
            <div>
              <Label htmlFor="phone" className="text-2xl font-semibold text-slate-700 mb-3 block">
                Phone Number *
              </Label>
              <Input
                id="phone"
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                className="h-16 text-2xl px-6"
                placeholder="+65 1234 5678"
                autoComplete="tel"
              />
            </div>

            {/* Store (Read-only, friendly label) */}
            {userStore && (
              <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                <Label className="text-xl font-semibold text-slate-700 mb-2 block">
                  Store Location
                </Label>
                <p className="text-2xl font-bold text-blue-700" data-testid="kiosk-form-store-friendly-label">
                  {getStoreLabel(userStore) || userStore}
                </p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-20 text-3xl font-bold bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300"
            >
              {isSubmitting ? (
                'Submitting...'
              ) : (
                'Submit'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default KioskRegisterPage;
